// What the zone editor hands to mapbox-gl-draw, as a pure function.
//
// The editor re-syncs the draw store from `loadedZones` after every save,
// delete, and day switch. That sync used to be an effect-local loop with
// deleteAll()+add()-under-fresh-id semantics and a dbId→featureId Map to
// bridge the two worlds; every hand-off through that Map was a chance for
// the "stale feature id" class of crash (Sentry PRODUCE-1Y). Now the db id
// IS the feature id, draw.set() diffs by id, and this module is the one
// place that decides what is visible and how it is stamped.

import { parsePolygon } from "@/types/domain";
import type { Tables } from "@/integrations/supabase/types";

export type ZoneRow = Pick<
  Tables<"festival_areas">,
  "id" | "name" | "area_type" | "stage_id" | "day_index" | "polygon"
>;

export interface ZoneFeature {
  type: "Feature";
  /** The festival_areas row id — feature identity and db identity are one. */
  id: string;
  properties: {
    name: string | null;
    user_name: string | null;
    area_type: string;
    user_area_type: string;
    stage_id: string | null;
    day_index: number | null;
    db_id: string;
  };
  geometry: GeoJSON.Geometry;
}

/**
 * The zones visible on a given map day: stage zones for that day (or
 * day-less singletons, which apply to every day), plus the always-on
 * location zones (boundary, media office, crew catering). Mirrors the
 * runtime LiveTracking filter — the editor used to show all days at once,
 * which stacked identical polygons of the same physical stage.
 */
export function visibleZonesForDay<Z extends Pick<ZoneRow, "area_type" | "day_index">>(
  zones: Z[],
  day: number,
): Z[] {
  return zones.filter((zone) => {
    if (zone.area_type !== "stage_zone") return true;
    if (zone.day_index === null) return true;
    return zone.day_index === day;
  });
}

/** Draw features for a day, keyed by db id. Rows whose polygon does not
 *  parse are skipped rather than crashing the sync. */
export function buildZoneFeatures(zones: ZoneRow[], day: number): ZoneFeature[] {
  return visibleZonesForDay(zones, day).flatMap((zone) => {
    const geometry = parsePolygon(zone.polygon);
    if (!geometry || !geometry.coordinates) return [];
    return [
      {
        type: "Feature" as const,
        id: zone.id,
        properties: {
          name: zone.name,
          user_name: zone.name,
          area_type: zone.area_type,
          user_area_type: zone.area_type,
          stage_id: zone.stage_id,
          day_index: zone.day_index,
          db_id: zone.id,
        },
        geometry: geometry as GeoJSON.Geometry,
      },
    ];
  });
}

/**
 * Whether the current selection must be cleared before draw.set().
 *
 * set() updates surviving ids in place — their selection and vertex
 * overlays stay valid, which is the point of stable ids. But a selected
 * feature that is about to DISAPPEAR (day switch, deleted elsewhere) would
 * leave direct_select pointing at nothing: the exact half-changed-mode
 * state PRODUCE-1Y documented. Clear first, only then set.
 */
export function needsSelectionClear(
  selectedIds: readonly string[],
  nextFeatures: readonly { id: string }[],
): boolean {
  if (selectedIds.length === 0) return false;
  const surviving = new Set(nextFeatures.map((f) => f.id));
  return selectedIds.some((id) => !surviving.has(id));
}
